import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  MapPin,
  Pencil,
  Plus,
  Route,
  Trash2,
  User as UserIcon,
} from "lucide-react";

import { ApiError } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { EmptyState } from "../../components/EmptyState";
import { Modal } from "../../components/Modal";
import {
  Badge,
  Button,
  Card,
  Cargando,
  ConfirmDialog,
  ErrorCarga,
  Input,
  PageHeader,
  Select,
  TableWrap,
  cn,
  controlClasses,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import { listarSectores, type SectorRead } from "../sectores/api";
import { listarUsuarios } from "../usuarios/api";
import {
  actualizarRuta,
  crearRuta,
  eliminarRuta,
  listarRutas,
  type RutaConDetalle,
  type RutaCreatePayload,
  type RutaUpdatePayload,
} from "./api";

export function RutasListPage(): JSX.Element {
  const [filtroSector, setFiltroSector] = useState<number | "todos">("todos");
  const [soloActivas, setSoloActivas] = useState(false);
  const [editing, setEditing] = useState<RutaConDetalle | "new" | null>(null);

  const sectoresQ = useQuery({
    queryKey: ["sectores"],
    queryFn: listarSectores,
  });

  const rutasQ = useQuery({
    queryKey: ["rutas", { sector_id: filtroSector, solo_activas: soloActivas }],
    queryFn: () => {
      const opts: { sector_id?: number; solo_activas?: boolean } = {
        solo_activas: soloActivas,
      };
      if (filtroSector !== "todos") opts.sector_id = filtroSector;
      return listarRutas(opts);
    },
  });

  return (
    <div className="space-y-4">
      <PageHeader
        title="Rutas"
        icon={Route}
        subtitle={
          <>
            La <strong>ruta</strong> es el recorrido operativo del operador en
            terreno (orden, asignación, día). Vive dentro de un{" "}
            <strong>sector</strong>, que es la dimensión legal/comercial (tarifas,
            reporte SISS). Si tu comité es chico, una sola ruta por sector
            alcanza; si tienes cientos de socios, pártelo en R01/R02/R03 con
            operadores y días distintos.
            {rutasQ.data && <> ({rutasQ.data.length} rutas)</>}
          </>
        }
        actions={
          <Button icon={Plus} onClick={() => setEditing("new")}>
            Nueva ruta
          </Button>
        }
      />

      <Card padding="sm" className="flex flex-wrap items-center gap-3 text-sm">
        <label className="flex items-center gap-2">
          <span className="text-xs uppercase text-neutral-500">Sector:</span>
          <select
            value={filtroSector === "todos" ? "todos" : String(filtroSector)}
            onChange={(e) =>
              setFiltroSector(
                e.target.value === "todos" ? "todos" : Number(e.target.value),
              )
            }
            className={cn(controlClasses, "w-auto")}
          >
            <option value="todos">Todos</option>
            {sectoresQ.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.codigo ? `${s.codigo} — ` : ""}
                {s.nombre}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5">
          <input
            type="checkbox"
            checked={soloActivas}
            onChange={(e) => setSoloActivas(e.target.checked)}
            className="rounded border-neutral-450 text-brand-600 focus:ring-brand-500"
          />
          <span className="text-xs text-neutral-700">Solo activas</span>
        </label>
      </Card>

      {editing && (
        <RutaForm
          ruta={editing === "new" ? null : editing}
          sectores={sectoresQ.data ?? []}
          onClose={() => setEditing(null)}
        />
      )}

      {rutasQ.isPending && <Cargando />}
      {rutasQ.isError && (
        <ErrorCarga
          error={rutasQ.error}
          mensaje="Error al cargar rutas."
          onReintentar={() => void rutasQ.refetch()}
        />
      )}

      {rutasQ.data && rutasQ.data.length === 0 && (
        <EmptyState
          icon={Route}
          title="Aún no hay rutas en este filtro"
          description="Las rutas agrupan los arranques que un operador recorre en orden. Crea una para empezar a asignar arranques y operadores."
        >
          <Button icon={Plus} onClick={() => setEditing("new")}>
            Crear ruta
          </Button>
        </EmptyState>
      )}

      {rutasQ.data && rutasQ.data.length > 0 && (
        <TableWrap label="Rutas">
          <table className="min-w-full divide-y divide-neutral-200 text-sm">
            <thead className="bg-neutral-50 text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th scope="col" className="px-3 py-2 text-left">Código</th>
                <th scope="col" className="px-3 py-2 text-left">Nombre</th>
                <th scope="col" className="px-3 py-2 text-left">Sector</th>
                <th scope="col" className="px-3 py-2 text-left">Operador</th>
                <th scope="col" className="px-3 py-2 text-right">Día lectura</th>
                <th scope="col" className="px-3 py-2 text-right"># Arranques</th>
                <th scope="col" className="px-3 py-2 text-right">Estado</th>
                <th scope="col" className="px-3 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {rutasQ.data.map((r) => (
                <RutaRow key={r.id} ruta={r} onEdit={() => setEditing(r)} />
              ))}
            </tbody>
          </table>
        </TableWrap>
      )}
    </div>
  );
}

function RutaRow({
  ruta,
  onEdit,
}: {
  ruta: RutaConDetalle;
  onEdit: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);

  const eliminar = useMutation({
    mutationFn: () => eliminarRuta(ruta.id),
    onSuccess: () => {
      toast.success(`Ruta "${ruta.nombre}" eliminada (arranques desasignados)`);
      void qc.invalidateQueries({ queryKey: ["rutas"] });
    },
    onError: (err) => {
      setConfirming(false);
      toast.error(err instanceof ApiError ? err.message : "No se pudo eliminar");
    },
  });

  return (
    <tr className="hover:bg-neutral-50">
      <td className="px-3 py-2 font-mono text-xs text-neutral-700">
        {ruta.codigo}
      </td>
      <td className="px-3 py-2 font-medium text-neutral-900">
        <Link
          to={`/rutas/${ruta.id}`}
          className="hover:text-brand-700 hover:underline inline-flex items-center gap-1"
        >
          {ruta.nombre}
          <ArrowRight className="h-3 w-3 opacity-40" />
        </Link>
      </td>
      <td className="px-3 py-2 text-neutral-600">
        <span className="inline-flex items-center gap-1">
          <MapPin className="h-3 w-3 opacity-50" />
          {ruta.sector_codigo ? `${ruta.sector_codigo} — ` : ""}
          {ruta.sector_nombre}
        </span>
      </td>
      <td className="px-3 py-2 text-neutral-600">
        {ruta.operador_nombre ? (
          <span className="inline-flex items-center gap-1">
            <UserIcon className="h-3 w-3 opacity-50" />
            {ruta.operador_nombre}
          </span>
        ) : (
          <span className="text-neutral-500 italic">sin asignar</span>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {ruta.dia_lectura ?? (
          <span title="Hereda del sector" className="text-neutral-500">
            {ruta.dia_lectura_efectivo}*
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">
        {ruta.cantidad_arranques}
      </td>
      <td className="px-3 py-2 text-right">
        <Badge tone={ruta.activa ? "emerald" : "slate"}>
          {ruta.activa ? "Activa" : "Inactiva"}
        </Badge>
      </td>
      <td className="px-3 py-2 text-right">
        <div className="flex items-center justify-end gap-3">
          <Button variant="link" size="sm" icon={Pencil} onClick={onEdit}>
            Editar
          </Button>
          <Button
            variant="linkDanger"
            size="sm"
            icon={Trash2}
            onClick={() => setConfirming(true)}
            disabled={eliminar.isPending}
          >
            Eliminar
          </Button>
        </div>
      </td>

      {confirming && (
        <ConfirmDialog
          open
          title={`Eliminar ruta "${ruta.nombre}"`}
          description={`Sus ${ruta.cantidad_arranques} arranque(s) quedarán sin asignar. La ruta se elimina de forma permanente.`}
          confirmLabel="Eliminar ruta"
          loading={eliminar.isPending}
          onCancel={() => setConfirming(false)}
          onConfirm={() => eliminar.mutate()}
        />
      )}
    </tr>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Form alta / edición
// ────────────────────────────────────────────────────────────────────────

function RutaForm({
  ruta,
  sectores,
  onClose,
}: {
  ruta: RutaConDetalle | null;
  sectores: SectorRead[];
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const editando = ruta !== null;

  const usuariosQ = useQuery({
    queryKey: ["usuarios"],
    queryFn: () => listarUsuarios(true),
  });

  const [sectorId, setSectorId] = useState(
    ruta?.sector_id ?? sectores[0]?.id ?? 0,
  );
  const [codigo, setCodigo] = useState(ruta?.codigo ?? "");
  const [nombre, setNombre] = useState(ruta?.nombre ?? "");
  const [operadorId, setOperadorId] = useState<number | "">(
    ruta?.operador_id ?? "",
  );
  const [diaLectura, setDiaLectura] = useState<string>(
    ruta?.dia_lectura != null ? String(ruta.dia_lectura) : "",
  );
  const [orden, setOrden] = useState(String(ruta?.orden_en_sector ?? 1));
  const [activa, setActiva] = useState(ruta?.activa ?? true);
  const [descripcion, setDescripcion] = useState(ruta?.descripcion ?? "");
  const [error, setError] = useState<string | null>(null);

  const sectorElegido = useMemo(
    () => sectores.find((s) => s.id === sectorId),
    [sectores, sectorId],
  );

  const crearMut = useMutation({
    mutationFn: (p: RutaCreatePayload) => crearRuta(p),
    onSuccess: (r) => {
      toast.success(`Ruta "${r.nombre}" creada`);
      void qc.invalidateQueries({ queryKey: ["rutas"] });
      onClose();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "No se pudo crear";
      setError(msg);
      toast.error(msg);
    },
  });

  const actualizarMut = useMutation({
    mutationFn: (p: RutaUpdatePayload) =>
      actualizarRuta((ruta as RutaConDetalle).id, p),
    onSuccess: (r) => {
      toast.success(`Ruta "${r.nombre}" actualizada`);
      void qc.invalidateQueries({ queryKey: ["rutas"] });
      onClose();
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError ? err.message : "No se pudo actualizar";
      setError(msg);
      toast.error(msg);
    },
  });

  const isPending = crearMut.isPending || actualizarMut.isPending;

  function submit(): void {
    setError(null);
    if (!editando && !sectorId) return setError("Sector requerido");
    if (!codigo.trim()) return setError("Código requerido");
    if (!nombre.trim()) return setError("Nombre requerido");

    let dl: number | null = null;
    if (diaLectura.trim() !== "") {
      const n = Number(diaLectura);
      if (!Number.isFinite(n) || n < 1 || n > 31)
        return setError(
          "Día de lectura debe estar entre 1 y 31 (vacío = hereda)",
        );
      dl = n;
    }

    const ord = Number(orden);
    if (!Number.isFinite(ord) || ord < 1)
      return setError("Orden en sector debe ser >= 1");

    if (editando) {
      const payload: RutaUpdatePayload = {
        codigo: codigo.trim(),
        nombre: nombre.trim(),
        operador_id: operadorId === "" ? null : operadorId,
        dia_lectura: dl,
        orden_en_sector: ord,
        activa,
        descripcion: descripcion.trim() || null,
      };
      actualizarMut.mutate(payload);
    } else {
      const payload: RutaCreatePayload = {
        sector_id: sectorId,
        codigo: codigo.trim(),
        nombre: nombre.trim(),
        operador_id: operadorId === "" ? null : operadorId,
        dia_lectura: dl,
        orden_en_sector: ord,
        descripcion: descripcion.trim() || null,
      };
      crearMut.mutate(payload);
    }
  }

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios([
    sectorId, codigo, nombre, operadorId, diaLectura, orden, activa, descripcion,
  ]);
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    crearMut.isPending || actualizarMut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title={editando ? "Editar ruta" : "Nueva ruta"}
      subtitle={editando ? `${ruta.codigo} — ${ruta.nombre}` : undefined}
      icon={Route}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={isPending}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={isPending}>
            {isPending
              ? editando
                ? "Guardando…"
                : "Creando…"
              : editando
                ? "Guardar cambios"
                : "Crear ruta"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {editando ? (
          <Input
            label="Sector"
            type="text"
            value={`${ruta.sector_codigo ? `${ruta.sector_codigo} — ` : ""}${ruta.sector_nombre}`}
            disabled
            title="El sector no se puede mover. Para cambiar de sector, recrea la ruta."
          />
        ) : (
          <Select
            label="Sector"
            required
            value={sectorId}
            onChange={(e) => setSectorId(Number(e.target.value))}
            disabled={isPending}
          >
            {sectores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.codigo ? `${s.codigo} — ` : ""}
                {s.nombre}
              </option>
            ))}
          </Select>
        )}
        <Input
          label="Código"
          required
          type="text"
          maxLength={20}
          value={codigo}
          onChange={(e) => setCodigo(e.target.value)}
          disabled={isPending}
          placeholder="R01"
          autoFocus
        />
        <Input
          label="Nombre"
          required
          type="text"
          maxLength={120}
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          disabled={isPending}
          placeholder="Ruta Norte / Calle Principal / etc."
        />
        <Select
          label="Operador asignado"
          value={operadorId === "" ? "" : String(operadorId)}
          onChange={(e) =>
            setOperadorId(e.target.value === "" ? "" : Number(e.target.value))
          }
          disabled={isPending || usuariosQ.isPending}
        >
          <option value="">— Sin asignar —</option>
          {usuariosQ.data?.map((u) => (
            <option key={u.id} value={u.id}>
              {u.nombre}
              {u.cargo ? ` · ${u.cargo}` : ""}
            </option>
          ))}
        </Select>
        <Input
          label="Día lectura"
          hint={`Vacío = hereda${
            sectorElegido ? ` el día ${sectorElegido.dia_lectura} del sector` : ""
          }`}
          type="number"
          min={1}
          max={31}
          value={diaLectura}
          onChange={(e) => setDiaLectura(e.target.value)}
          disabled={isPending}
          placeholder="hereda"
        />
        <Input
          label="Orden en el sector"
          type="number"
          min={1}
          value={orden}
          onChange={(e) => setOrden(e.target.value)}
          disabled={isPending}
        />
        <Input
          label="Descripción"
          type="text"
          value={descripcion}
          onChange={(e) => setDescripcion(e.target.value)}
          disabled={isPending}
        />
        {editando && (
          <div>
            <span className="block text-sm font-medium text-neutral-700">
              Estado
            </span>
            <label className="mt-1.5 inline-flex items-center gap-2 text-sm text-neutral-700">
              <input
                type="checkbox"
                checked={activa}
                onChange={(e) => setActiva(e.target.checked)}
                disabled={isPending}
                className="rounded border-neutral-450 text-brand-600 focus:ring-brand-500"
              />
              Activa
            </label>
          </div>
        )}
      </div>

      {error && <p className="mt-3 text-sm text-rose-700">{error}</p>}
      {dialogoDescarte}
    </Modal>
  );
}
